import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import { reduxForm, Field } from 'redux-form/immutable';
import { change } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { debounce, get, keys, map, includes, pull, split, times, filter } from 'lodash';
import moment from 'moment';
import classnames from 'classnames';
import ContentLoader from 'react-content-loader';
import { MuiThemeProvider as MuiThemeProviderNext, withStyles } from '@material-ui/core/styles';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core';
import { NotificationsOff as NotificationsOffIcon } from '@material-ui/icons';

import { capitalizeFirstLetter } from '@lib/services/stringsService';
import { selectCurrentUser } from '@lib/selectors';
import isOnEmbedPage from '@lib/utils/isOnEmbedPage';
import { makeSelectFormField } from 'containers/App/selectors';
import { durationList } from 'utils/time-defaults';
import { createValidator, required, shouldBeInFuture } from 'utils/validation';
import { makeMuiThemeLumaLegacy } from 'embeds/themes';

import { selectPermissions } from 'selectors';

import ChipInputField from './ChipInputField';
import { Button } from '../Button';
import { TextField } from '../TextField';
import { Typography } from '../Typography';
import InfiniteScroll from '../InfiniteScroll';
import FormDatePicker from '../ReduxForm/FormDatePicker';
import FormSelectField from '../ReduxForm/FormSelectField';
import FormTimePicker from '../ReduxForm/FormTimePicker';
import PatientCustomError from '../PatientCustomError';
import { DialogHeader } from '../DialogHeader';

import {
  makeSelectProviders,
  makeSelectFacilities,
  makeSelectAppointmentTypes,
  makeSelectWaitlists,
  makeSelectLoading,
  makeSelectPatients,
  makeSelectTags,
  makeSelectWaitlistsGlobalCount,
  makeSelectIsBullkWaitlists,
  makeSelectWaitlistsQueryId,
} from './selectors';
import {
  clearTags,
  clearWaitlists,
  loadProviders,
  loadFacilities,
  loadAppointmentTypes,
  loadTags,
  loadWaitlists,
  sendOffer,
} from './actions';
import { FORM_NAME } from './constants';

const styles = ({
  custom: {
    palette: { colorGrayishBlue, colorVeryLightGray },
    fontSizes,
  },
  palette: {
    common: { white },
  },
  spacing: { unit },
}) => ({
  bulkWaitlistEmailWrapper: {
    marginTop: unit * 7,
  },
  bulkWaitlistEmailWrapperBold: {
    fontWeight: 600,
  },
  content: {
    display: 'flex',
    flex: 1,
    padding: '0px !important',
    overflow: 'hidden',
    maxHeight: 550,
    background: white,
  },
  contentLoader: {
    height: 64,
    width: 432,
  },
  contentLoaderWrapper: {
    marginTop: unit * 3,
  },
  doNotContact: {
    marginLeft: unit,
  },
  doNotContactCheckbox: {
    opacity: 0.5,
  },
  form: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  gutter: {
    marginBottom: unit,
  },
  section: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflowY: 'auto',
    padding: `16px ${unit * 2}px ${unit * 2}px ${unit * 2}px`,
    '&:first-child': {
      borderRight: `1px solid ${colorGrayishBlue}`,
    },
  },
  tab: {
    textTransform: 'initial',
  },
  tabs: {
    maxWidth: 350,
  },
  tabsContainer: {
    background: colorVeryLightGray,
    borderBottom: `1px solid ${colorGrayishBlue}`,
  },
  title: {
    fontSize: fontSizes.bodyG,
  },
  waitlistDoNotContactName: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  waitlistDisabledName: {
    textDecoration: 'line-through',
  },
  waitlistDisabledText: {
    display: 'flex',
    alignItems: 'center',
  },
  waitlistDisabledIcon: {
    marginLeft: unit / 2,
    fontSize: fontSizes.bodyG,
  },
  waitlistsContainer: {
    height: 413,
    overflowY: 'auto',
    marginTop: unit,
  },
});

const validate = createValidator({
  date: [required, shouldBeInFuture('date')],
  duration: required,
  facility: required,
  provider: required,
  time: [required, shouldBeInFuture('time')],
});

const numberWithCommas = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

class DialogSendOffers extends PureComponent {
  constructor(props) {
    super(props);

    this.renderWaitlistItem = this.renderWaitlistItem.bind(this);

    this.state = {
      tabSelected: 'patients',
      emailValidation: '',
    };
  }

  componentWillMount() {
    const {
      currentUser,
      dispatchLoadAppointmentTypes,
      dispatchLoadFacilities,
      dispatchLoadProviders,
      dispatchLoadTags,
      filters: { provider },
      initialize,
      tags,
      touch,
    } = this.props;
    const data = {
      provider,
      facility: false,
      patients: [],
      date: moment(),
      time: moment().add(1, 'minutes'),
      duration: get(currentUser, 'settings.appointment.duration'),
      appointmentType: 'any',
      tags,
      availability: false,
      waitlistsSelected: [],
    };

    initialize(data);

    // show required fields
    touch(...keys(data));

    dispatchLoadProviders('', provider);
    dispatchLoadFacilities('', false);
    dispatchLoadAppointmentTypes();
    dispatchLoadTags();
  }

  componentWillReceiveProps({ waitlists: waitlistsNext }) {
    const { waitlists } = this.props;

    if (waitlistsNext && waitlistsNext !== waitlists) {
      this.handleChange('waitlistsSelected', map(waitlistsNext, '_id') || []);
    }
  }

  componentWillUnmount() {
    const { dispatchClearTags, dispatchClearWaitlists } = this.props;

    dispatchClearWaitlists();
    dispatchClearTags();
  }

  handleChange = (field, value) => {
    const { dispatchChange } = this.props;

    dispatchChange(field, value);
  };

  handleFilterChange = (value) => {
    const { dispatchLoadWaitlists, formValues } = this.props;
    const body = formValues && formValues.toJS();
    const filters = { ...body, ...value };

    this.handleChange('waitlistsSelected', []);

    if (filters.date && filters.time && filters.duration && filters.facility && filters.provider) {
      dispatchLoadWaitlists({
        ...filters,
        appointmentType: filters.appointmentType !== 'any' ? split(filters.appointmentType, ',') : null,
        tags: filters.tags.length > 0 ? split(filters.tags, ',') : undefined,
      });
    }
  };

  handlePatientClick = (patient) => {
    if (!isOnEmbedPage()) {
      browserHistory.push(`/patients/${patient._id}/chat`);
    }
  };

  handleSubmit = (values) => {
    const {
      dispatchSubmitOffer,
      filters,
      onRequestClose,
      isBulkWaitlists,
      waitilistsGlobalCount,
      waitlists,
      waitlistsQueryId,
    } = this.props;
    const body = values.toJS();
    const { waitlistsSelected } = body;

    // All patients on waitlist are receiving offers,
    // so we need to remove those with doNotContact from the list
    // before submitting
    const idsNotContact = map(filter(waitlists, 'patient.doNotContact'), '_id');
    const waitlistsSelectedToSend = filter(waitlistsSelected, (id) => !idsNotContact.includes(id));

    if (body.availability) {
      if (
        moment(filters.date).format('YYYY-MM-DD') !== moment(body.date).format('YYYY-MM-DD') ||
        moment(body.date).format('YYYY-MM-DD hh:mm:ss') !== moment(body.time).format('YYYY-MM-DD hh:mm:ss') ||
        body.provider !== filters.provider ||
        body.facility !== filters.facility ||
        body.duration !== filters.duration
      ) {
        body.availability = undefined;
      }
    }

    body.appointmentType = body.appointmentType !== 'any' ? split(body.appointmentType, ',') : null;

    delete body.waitlistsSelected;

    const waitlistsCount =
      isBulkWaitlists && waitilistsGlobalCount > 100 ? waitilistsGlobalCount : waitlistsSelectedToSend.length;

    dispatchSubmitOffer(body, waitlistsSelectedToSend, waitlistsCount, waitlistsQueryId);
    onRequestClose();
  };

  handleTabChange = (event, value) => {
    this.setState({ tabSelected: value });
  };

  handleWaitlistSelected = (_id, checked) => {
    const { formValues } = this.props;
    const body = formValues && formValues.toJS();
    const waitlistsSelected = [...body.waitlistsSelected];

    if (checked) {
      waitlistsSelected.push(_id);
    } else {
      pull(waitlistsSelected, _id);
    }

    this.handleChange('waitlistsSelected', [...waitlistsSelected]);
  };

  renderFooter(isBusy) {
    const {
      waitilistsGlobalCount,
      currentUser,
      formValues,
      handleSubmit,
      isBulkWaitlists,
      onRequestClose,
      submitting,
    } = this.props;
    const body = formValues && formValues.toJS();
    const { emailValidation, tabSelected } = this.state;

    if (isBusy && tabSelected !== 'offer') return null;

    const userEmail = get(currentUser, 'email');
    const hasError = isBulkWaitlists && userEmail !== emailValidation;

    const buttonLabel = isBulkWaitlists ? `Send ${numberWithCommas(waitilistsGlobalCount)} offers` : 'Send offers';

    return (
      <DialogActions>
        <Button color="primary" onClick={onRequestClose} variant="text">
          Cancel
        </Button>
        <Button
          rounded
          color="secondary"
          form={FORM_NAME}
          type="submit"
          disabled={hasError || submitting || get(body, 'waitlistsSelected.length', 0) === 0}
          onClick={handleSubmit(this.handleSubmit)}
          variant="contained"
        >
          {buttonLabel}
        </Button>
      </DialogActions>
    );
  }

  renderWaitlistItem({ _id, bulkWaitlist, disabled, duration, patient: { name, doNotContact } }) {
    const { classes, formValues } = this.props;
    const body = formValues && formValues.toJS();
    let secondaryText;

    if (disabled) {
      secondaryText = (
        <PatientCustomError text="We can’t send an offer to this patient" IconProps={{ color: 'secondary' }} />
      );
    } else if (doNotContact) {
      secondaryText = (
        <span>
          <NotificationsOffIcon fontSize="small" className={classes.secondaryIcon} />
          <span className={classes.doNotContact}>Do not contact</span>
        </span>
      );
    } else if (duration) {
      secondaryText = `${duration} minutes appointment`;
    } else {
      secondaryText = 'Any duration';
    }

    return (
      <ListItem
        key={_id}
        disableGutters
        className={classnames({
          [classes.doNotContactCheckbox]: doNotContact,
        })}
      >
        <Checkbox
          checked={disabled || doNotContact ? false : includes(get(body, 'waitlistsSelected', []), _id)}
          onChange={(event, checked) => this.handleWaitlistSelected(_id, checked)}
          color="primary"
          disabled={bulkWaitlist || disabled || doNotContact}
        />
        <ListItemText
          primary={
            <div
              className={classnames({
                [classes.waitlistDisabledName]: disabled,
                [classes.waitlistDoNotContactName]: doNotContact,
              })}
            >
              {name}
            </div>
          }
          secondary={secondaryText}
        />
      </ListItem>
    );
  }

  renderList() {
    const { loading, waitlists } = this.props;

    if (loading || !waitlists) return null;

    return (
      <InfiniteScroll useWindow={false} initialLoad={false} threshold={50}>
        <List>{map(waitlists, this.renderWaitlistItem)}</List>
      </InfiniteScroll>
    );
  }

  renderLoadingContent() {
    const { classes, loading } = this.props;

    if (!loading) return null;

    return (
      <div className={classes.contentLoaderWrapper}>
        {times(5).map((index) => (
          <ContentLoader
            className={classes.contentLoader}
            key={`content-loader-${index}`}
            height={64}
            width={432}
            speed={2}
            primaryColor="#f3f3f3"
            secondaryColor="#ecebeb"
          >
            <rect x="0" y="80" rx="3" ry="3" width="350" height="6" />
            <rect x="0" y="100" rx="3" ry="3" width="380" height="6" />
            <rect x="0" y="120" rx="3" ry="3" width="201" height="6" />
            <rect x="19" y="21" rx="0" ry="0" width="24" height="24" />
            <rect x="57" y="16" rx="0" ry="0" width="360" height="16" />
            <rect x="57" y="36" rx="0" ry="0" width="286" height="12" />
            <rect x="369" y="47" rx="0" ry="0" width="2" height="1" />
          </ContentLoader>
        ))}
      </div>
    );
  }

  renderBulkWaitlists() {
    const { emailValidation } = this.state;
    const { classes, currentUser, waitilistsGlobalCount } = this.props;

    const title = `${numberWithCommas(waitilistsGlobalCount)} patients found based on your selections.`;
    const userEmail = get(currentUser, 'email');

    const handleEmailType = (e) => this.setState({ emailValidation: e.target.value });
    const hasError = !!emailValidation && userEmail !== emailValidation;

    return (
      <div className={classes.section}>
        <div>
          <Typography variant="h1W600F24" color="secondary">
            {title}
          </Typography>
          <div className={classes.gutter} />
          <Typography variant="supportW400F14" color="supportMidnight">
            ️ If the number of patients found exceeds 100, you can no longer select or deselect patients from the list.
          </Typography>
        </div>
        <div className={classes.bulkWaitlistEmailWrapper}>
          <Typography variant="h3W500F17" color="supportMidnight">
            Please double check your selections and
            <span className={classes.bulkWaitlistEmailWrapperBold}>
              &nbsp;type the email associated with your account&nbsp;
            </span>
            to confirm.
          </Typography>
          <div className={classes.gutter} />
          <TextField
            error={hasError}
            helperText={hasError ? "Email doesn't match." : ''}
            size="small"
            label="Email"
            placeholder="Type your e-mail"
            required
            rounded
            value={emailValidation}
            onChange={handleEmailType}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </div>
      </div>
    );
  }

  renderWaitlists(isBusy) {
    const { classes, loading, waitlists, waitilistsGlobalCount } = this.props;
    const { tabSelected } = this.state;

    const totalWaitlists =
      waitilistsGlobalCount > waitlists.length && waitilistsGlobalCount > 100
        ? waitilistsGlobalCount
        : get(waitlists, 'length', 'No');
    const title = !loading && waitlists ? `${totalWaitlists} waiting patients found` : '';

    if (!(!isBusy || tabSelected === 'offer')) return null;

    return (
      <div className={classes.section}>
        <MuiTypography className={classes.title}>{title}</MuiTypography>
        <div className={classes.waitlistsContainer}>
          {this.renderLoadingContent()}
          {this.renderList()}
        </div>
      </div>
    );
  }

  renderPatients(isBusy) {
    const { classes, patients } = this.props;
    const { tabSelected } = this.state;

    if (!isBusy || tabSelected === 'offer') return null;

    return (
      <div className={classes.section}>
        <MuiTypography className={classes.title}>Patients</MuiTypography>
        <div className={classes.waitlistsContainer}>
          <InfiniteScroll threshold={50} useWindow initialLoad={false}>
            <List>
              {map(patients, (patient) => (
                <ListItem button key={patient._id} onClick={() => this.handlePatientClick(patient)}>
                  <ListItemText primary={patient.name} secondary={capitalizeFirstLetter(patient.status)} />
                </ListItem>
              ))}
            </List>
          </InfiniteScroll>
        </div>
      </div>
    );
  }

  renderAppointmentType(isBusy) {
    const { appointmentTypes, formValues } = this.props;
    const body = formValues && formValues.toJS();
    const appointmentType = get(body, 'appointmentType');

    if (!(appointmentTypes && (!isBusy || (appointmentType && appointmentType !== 'any')))) return null;

    return (
      <Field
        component={FormSelectField}
        disableUnderline={false}
        label="Appointment Type"
        multiple
        name="appointmentType"
        onChange={(value) => this.handleFilterChange({ appointmentType: value })}
        options={appointmentTypes}
        showDefaultOption
      />
    );
  }

  renderTags(isBusy) {
    const { permissions, tags } = this.props;
    const { hasAccessWaitlistsTags } = permissions;

    if (isBusy || !hasAccessWaitlistsTags) return null;

    return (
      <Field
        component={ChipInputField}
        disableUnderline={false}
        label="Tags"
        name="tags"
        multiple
        onChange={(value) => {
          this.handleFilterChange({ tags: value });
        }}
        tags={tags || []}
      />
    );
  }

  renderDuration() {
    return (
      <Field
        component={FormSelectField}
        disableUnderline={false}
        label="Duration"
        name="duration"
        onChange={(value) => this.handleFilterChange({ duration: value })}
        options={durationList}
        showRequiredMark
      />
    );
  }

  renderFacility(isBusy) {
    const { dispatchLoadFacilities, facilities } = this.props;
    const slowSearch = debounce(dispatchLoadFacilities, 300);

    return (
      <Field
        component={FormSelectField}
        disabled={isBusy}
        disableUnderline={false}
        floatingLabel
        label="Facility"
        name="facility"
        onChange={(value) => this.handleFilterChange({ facility: value })}
        onFilter={slowSearch}
        options={facilities || []}
        showRequiredMark
      />
    );
  }

  renderProvider(isBusy) {
    const { dispatchLoadProviders, providers } = this.props;
    const slowSearch = debounce(dispatchLoadProviders, 300);

    return (
      <Field
        component={FormSelectField}
        disableUnderline={false}
        disabled={isBusy}
        floatingLabel
        label="Provider"
        name="provider"
        onChange={(value) => this.handleFilterChange({ provider: value })}
        onFilter={slowSearch}
        options={providers || []}
        showRequiredMark
      />
    );
  }

  renderTime(isBusy) {
    if (isBusy) return null;

    return (
      <Field
        component={FormTimePicker}
        fullWidth
        label="Time"
        name="time"
        onChange={(event, value) => this.handleFilterChange({ time: value })}
      />
    );
  }

  renderDate(isBusy) {
    const { formValues } = this.props;
    const body = formValues && formValues.toJS();

    if (isBusy && body) {
      const dateTime = moment(body.date).format('MMMM DD, YYYY h:mm A');

      return <MuiTextField disabled fullWidth disabledUnderline label="Date" value={dateTime} />;
    }

    return (
      <Field
        component={FormDatePicker}
        fullWidth
        label="Date"
        name="date"
        onChange={(event, value) => this.handleFilterChange({ date: value })}
      />
    );
  }

  renderFiltersTitle(isBusy) {
    const { classes } = this.props;

    if (!isBusy) return null;

    return <MuiTypography className={classes.title}>Appointment Details</MuiTypography>;
  }

  renderFilters(isBusy) {
    const { classes, theme } = this.props;
    const legacyTheme = makeMuiThemeLumaLegacy(theme);
    return (
      <MuiThemeProviderNext theme={legacyTheme}>
        <div className={classes.section}>
          <div>
            {this.renderFiltersTitle(isBusy)}
            {this.renderDate(isBusy)}
            {this.renderTime(isBusy)}
            {this.renderProvider(isBusy)}
            {this.renderFacility(isBusy)}
            {this.renderDuration()}
            {this.renderAppointmentType(isBusy)}
            {this.renderTags(isBusy)}
          </div>
        </div>
      </MuiThemeProviderNext>
    );
  }

  renderTabs(isBusy) {
    const { classes, filters } = this.props;
    const { tabSelected } = this.state;
    const length = get(filters, 'patients.length', 0);

    if (!isBusy) return null;

    return (
      <div className={classes.tabsContainer}>
        <Tabs className={classes.tabs} onChange={this.handleTabChange} value={tabSelected}>
          <Tab className={classes.tab} label={`Appointments (${length})`} value="patients" />
          <Tab className={classes.tab} label="Offer More Patients" value="offer" />
        </Tabs>
      </div>
    );
  }

  renderContent(isBusy) {
    const { classes, isBulkWaitlists, handleSubmit } = this.props;

    return (
      <DialogContent className={classes.content}>
        {this.renderTabs(isBusy)}
        <form className={classes.form} id={FORM_NAME} onSubmit={handleSubmit(this.handleSubmit)}>
          {this.renderFilters(isBusy)}
          {this.renderPatients(isBusy)}
          {!isBulkWaitlists && this.renderWaitlists(isBusy)}
          {isBulkWaitlists && this.renderBulkWaitlists()}
        </form>
      </DialogContent>
    );
  }

  render() {
    const { classes, open, onRequestClose, filters } = this.props;
    const isBusy = get(filters, 'freebusy', 'free') === 'busy';
    const date = get(filters, 'date');
    const title =
      isBusy && date ? `${moment(date).format('h:mma')} appointment` : 'Send appointment offers to waiting patients';

    return (
      <Dialog classes={{ paper: classes.content }} open={open} onClose={onRequestClose} maxWidth="md" fullWidth>
        <DialogHeader title={title} onRequestClose={onRequestClose} />
        {this.renderContent(isBusy)}
        {this.renderFooter(isBusy)}
      </Dialog>
    );
  }
}

DialogSendOffers.propTypes = {
  appointmentTypes: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  classes: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  dispatchClearTags: PropTypes.func.isRequired,
  dispatchClearWaitlists: PropTypes.func.isRequired,
  dispatchLoadAppointmentTypes: PropTypes.func.isRequired,
  dispatchLoadFacilities: PropTypes.func.isRequired,
  dispatchLoadProviders: PropTypes.func.isRequired,
  dispatchLoadWaitlists: PropTypes.func.isRequired,
  dispatchSubmitOffer: PropTypes.func.isRequired,
  facilities: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  filters: PropTypes.object,
  formValues: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  patients: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  permissions: PropTypes.object,
  providers: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  submitting: PropTypes.bool,
  touch: PropTypes.func.isRequired,
  isBulkWaitlists: PropTypes.bool,
  waitlists: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  waitilistsGlobalCount: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  waitlistsQueryId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

const mapStateToProps = createStructuredSelector({
  appointmentTypes: makeSelectAppointmentTypes(),
  currentUser: selectCurrentUser,
  facilities: makeSelectFacilities(),
  formValues: makeSelectFormField(FORM_NAME),
  loading: makeSelectLoading(),
  patients: makeSelectPatients(),
  permissions: selectPermissions,
  providers: makeSelectProviders(),
  tags: makeSelectTags(),
  isBulkWaitlists: makeSelectIsBullkWaitlists(),
  waitlists: makeSelectWaitlists(),
  waitilistsGlobalCount: makeSelectWaitlistsGlobalCount(),
  waitlistsQueryId: makeSelectWaitlistsQueryId(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchChange: (field, value) => {
    dispatch(change(FORM_NAME, field, value));
  },
  dispatchClearTags: () => {
    dispatch(clearTags());
  },
  dispatchClearWaitlists: () => {
    dispatch(clearWaitlists());
  },
  dispatchLoadAppointmentTypes: () => {
    dispatch(loadAppointmentTypes());
  },
  dispatchLoadFacilities: (query) => {
    dispatch(loadFacilities(query));
  },
  dispatchLoadProviders: (query, initialValue) => {
    dispatch(loadProviders(query, initialValue));
  },
  dispatchLoadTags: () => {
    dispatch(loadTags());
  },
  dispatchSubmitOffer: (values, waitlists, waitlistsCount, waitlistsQueryId) => {
    dispatch(sendOffer(values, waitlists, waitlistsCount, waitlistsQueryId));
  },
  dispatchLoadWaitlists: (filters) => {
    dispatch(loadWaitlists(filters));
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withForm = reduxForm({
  form: FORM_NAME,
  validate,
});

export default compose(withConnect, withForm, withStyles(styles, { withTheme: true }))(DialogSendOffers);
